import React from 'react';
import {Box, Link as JoyLink, Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {DepartmentIcon, ServiceProviderIcon, WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import {getServiceUnitSourceInfo} from "../../utils/serviceUnitSource";
import ServiceUnitContent, {ResolvedServiceUnitInfo} from "./ServiceUnitContent";

interface Props {
    resolved: ResolvedServiceUnitInfo;
    // 'customer': the viewer is served by this unit ("your contacts");
    // 'provider': the viewer belongs to the service organisation and looks at
    // which unit serves the customer's element
    perspective: 'customer' | 'provider';
    // makes the ANCESTOR_NODE origin chip navigate the tree to that ancestor
    onNavigateToOrigin?: (inheritedFrom: NonNullable<ResolvedServiceUnitInfo['inheritedFrom']>) => void;
    // CRM deep link for users who may change the client-level assignment
    changeHref?: string;
}

/**
 * "Responsible Service Center" block of the node contact tab (IBM-972): the
 * same WidgetBox the hero cards use, with the same body as the dashboard
 * widget and the asset/system hero card — in its `detail` variant, so the
 * hotline and named contacts share the typography and two-column grid of the
 * contact persons listed below the box. The header carries the
 * source-dependent title (a fallback is titled "Central service contact",
 * never as a dedicated unit) and the CRM "Change" link.
 */
const ServiceUnitInfoPanel = ({resolved, perspective, onNavigateToOrigin, changeHref}: Props) => {
    const {t} = useTranslation();
    const unit = resolved.unit;
    const sourceInfo = getServiceUnitSourceInfo(resolved.source, t, {
        ancestorTitle: resolved.inheritedFrom?.title,
        unitType: unit?.type,
        perspective,
    });

    if (!unit) return null;

    const TypeIcon = unit.type === 'DEPARTMENT' ? DepartmentIcon : ServiceProviderIcon;

    return (
        <Box data-testid="service-unit-panel">
            {/* border-box: WidgetBox is width 100% plus padding — without it the
                tab's scroll container gets a horizontal scrollbar */}
            <WidgetBox sx={{backgroundColor: '#fff', boxSizing: 'border-box'}}>
                {/* WidgetBox's own title has no action slot — same markup, plus the link */}
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'}}>
                    <Typography level="titleMediumLight" sx={{display: 'flex', alignItems: 'center', color: '#333', minWidth: 0}}>
                        <TypeIcon size={26} color="#333"/>
                        <Typography level="titleMediumLight" noWrap sx={{pl: '8px'}}>
                            {sourceInfo.title}
                        </Typography>
                    </Typography>
                    {changeHref && (
                        <JoyLink level="bodySmall" href={changeHref} data-testid="service-unit-change" sx={{flexShrink: 0}}>
                            {t('Change')}
                        </JoyLink>
                    )}
                </Box>
                <ServiceUnitContent
                    resolved={resolved}
                    onNavigateToOrigin={onNavigateToOrigin}
                    perspective={perspective}
                    showTypeIcon={false}
                    variant="detail"
                />
            </WidgetBox>
        </Box>
    );
};

export default ServiceUnitInfoPanel;
